import { cardData } from "@/hooks/useBuscadorHook";

// Spanish plural form per cardData category. cardData itself stores singular
// labels ("Casa", "Departamento", ...); plurals don't always follow `+s`
// ("Local comercial" → "Locales comerciales"), so a static lookup is cleaner
// than runtime conjugation.
const CATEGORY_PLURAL = {
    HOUSE: "Casas",
    APARTMENT: "Departamentos",
    TERRAIN: "Terrenos",
    OFFICE: "Oficinas",
    SHOP_SHOPPING_CENTER: "Locales comerciales",
    WAREHOUSE: "Bodegas",
};

const OPERATION_VERB = {
    SALE: "en venta",
    LEASE: "en renta",
    PRESALE: "en preventa",
};

const FALLBACK_TITLE = "Buscar propiedades · Grupo Guía";

// Map state.type[] (one or more property-type enums like HOUSE, DUPLEX_HOUSE)
// to a single Spanish plural noun. cardData groups the 17 backend enums into
// 6 user-facing categories; if all selected types map to ONE category, use
// that category's plural ("Casas"). If multiple categories are touched (or
// no types selected at all), fall back to "Propiedades" — the generic noun.
const typesToNoun = (types) => {
    if (!types || types.length === 0) return "Propiedades";
    const categories = new Set();
    for (const type of types) {
        const entry = cardData.find((c) => c.types.includes(type));
        if (entry) categories.add(entry.key);
    }
    if (categories.size !== 1) return "Propiedades";
    const [only] = categories;
    return CATEGORY_PLURAL[only] || "Propiedades";
};

// Operation → verb phrase. Empty / multi-op (= Todas) returns null so the
// caller drops the verb segment entirely (no "Casas Todas en Zapopan").
const operationToVerb = (operation) => {
    if (!Array.isArray(operation) || operation.length !== 1) return null;
    return OPERATION_VERB[operation[0]] || null;
};

// User-entered location can be a Places-autocomplete blob like "Providencia,
// Guadalajara, Jalisco, Mexico". For the tab title only the first segment is
// useful; trailing region/country labels make the title unwieldy.
const shortLocation = (q) => {
    if (!q) return null;
    const trimmed = String(q).trim();
    if (!trimmed) return null;
    const firstSegment = trimmed.split(",")[0].trim();
    return firstSegment || null;
};

// Compose a tab title that reflects the active search.
//   No filters / no location → the fallback title: the static "Buscar
//     propiedades · Grupo Guía" on /Buscar, or the page's own head title
//     on a dedicated search page (cold-load reads like that page's "fresh
//     start" surface, not like a filtered result set)
//   Has location OR specific type → composes "<noun> <verb> en <loc>"
//     dropping any segment that's empty
//
// Options (all optional — omitted = plain /Buscar behavior):
//   fallbackTitle:    used instead of the static fallback.
//   defaultType:      the page's preset type set; a selection equal to it
//                     does not count as "specific" (a type page's own
//                     preset shows the page title, not a composed one).
//   fallbackLocation: location segment when the user hasn't typed one (a
//                     city page composes "Casas en venta en <city>").
const sameSet = (a, b) => {
    const left = Array.isArray(a) ? a : [];
    const right = Array.isArray(b) ? b : [];
    return left.length === right.length && left.every((v) => right.includes(v));
};

const deriveSearchTitle = (state, opts = {}) => {
    const fallback = opts.fallbackTitle || FALLBACK_TITLE;
    if (!state) return fallback;
    const location = shortLocation(state.q);
    const hasSpecificType =
        (state.type?.length ?? 0) > 0 && !sameSet(state.type, opts.defaultType ?? []);
    if (!location && !hasSpecificType) return fallback;
    const noun = typesToNoun(state.type);
    const verb = operationToVerb(state.operation);
    const effectiveLocation = location || opts.fallbackLocation || null;
    const where = effectiveLocation ? `en ${effectiveLocation}` : null;
    const phrase = [noun, verb, where].filter(Boolean).join(" ");
    return `${phrase} · Grupo Guía`;
};

export default deriveSearchTitle;
export { FALLBACK_TITLE };
